<template>
  <div :key="resetKey" class="container container-padding product-view">
    <component
      :is="modal ? ModalComponent : 'div'"
      ref="componentRef"
      :style="modal ? '' : style"
      :modal-style="style"
      :class="[
        'product',
        {
          'edit-mode': editMode,
          'create-mode': creatingNewProduct,
          'position-relative': !modal,
        },
      ]"
      :body-style="`padding-bottom: 0; margin-top: 0; max-width: 100vw; ${loading ? '' : `padding-top: ${paddingTop}`};`"
      animation="slide-right"
      :deviating-opening-animation="navigatedBackwards ? 'slide-left' : undefined"
      :animation-speed="0.15"
      :navigation-confirm-blocks-close="!isPristine"
      :block-close="deletePromptActive || showVersionModal"
      @close="handleClose()"
    >
      <template v-if="activeProduct && propertyGroups">
        <LoaderComponent
          v-if="loading || actionPending === 'editProduct'"
          :text="loading ? undefined : `${appTranslate('Saving')}...`"
          :style="`margin-top: -${paddingTop}`"
        />

        <template v-if="editMode">
          <div class="modal-shadow shadow-top" />
          <div class="modal-shadow shadow-left" />
          <div class="modal-shadow shadow-right" />
        </template>

        <div v-if="creatingNewProduct">
          <h2>{{ appTranslate('Create new product') }}</h2>

          <h4>{{ appTranslate('Base on product') }}</h4>

          <ProductSearchComponent
            include-culture-exclusive-data
            :data-culture="store.dataCulture"
            :included-extra-countries="includeAllCulturesSearch ? store.availableCountries : undefined"
            load-more-inline
            :placeholder="appTranslate('Search product')"
            @select="setProductStartingPoint($event.value)"
          />
          <div>
            <label class="ml-2 user-select-none">
              <input v-model="includeAllCulturesSearch" type="checkbox" />
              {{ appTranslate('Include products from all cultures (countries/languages)') }}
            </label>
          </div>

          <div v-if="productStartingPoint" class="mt-4">
            <div class="font-weight-bold">
              {{ appTranslate('Based on') }}
            </div>
            <ProductMiniCard :product="productStartingPoint">
              <button class="btn ml-2" style="font-size: 2em" @click="setProductStartingPoint()">&times;</button>
            </ProductMiniCard>
          </div>

          <div class="create-new-separator" />
        </div>

        <h2 v-else-if="editMode">
          {{ appTranslate('Editing mode') }}
        </h2>

        <section class="presentation mt-0">
          <!-- Product type -->
          <PropertyComponent
            v-if="productTypeProperty"
            :ref="productTypeProperty.defaultHeader"
            :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, productTypeProperty)"
            class="mb-3"
            :property="productTypeProperty"
            :product="activeProduct"
            :product-input="productInput"
            :edit-mode="editMode"
            show-property-info
            :undo-icon="creatingNewProduct ? null : 'fa-undo'"
            tag="span"
            @input="currentFieldHasStagedChanges = !!$event.containsChanges"
            @set="currentFieldHasStagedChanges = undefined"
            @contextmenu="openContextMenu($event, productTypeProperty)"
          >
            <template v-if="!editMode" #static="{ value }">
              <i :class="getProductTypeIconClass(value as ProductType)" />
            </template>
          </PropertyComponent>

          <!-- Categories -->
          <PropertyComponent
            v-if="categoriesProperty"
            :ref="categoriesProperty.defaultHeader"
            :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, categoriesProperty)"
            class="mb-4 categories"
            :property="categoriesProperty"
            :edit-mode="editMode"
            :product="activeProduct"
            activate-filter-links
            :product-input="productInput"
            show-property-info
            :undo-icon="creatingNewProduct ? null : 'fa-undo'"
            tag="span"
            @contextmenu="openContextMenu($event, categoriesProperty)"
          />

          <div class="row">
            <!-- Image in focus -->
            <div class="col col-md-12 col-lg-6 px-3">
              <template v-if="activeMedia">
                <ImprovedZoomableImage
                  v-if="activeMedia.type === 'image'"
                  :key="activeMedia.id"
                  :preview-src="getImageUrl(activeMedia.id, 640)"
                  :zoom-src="activeMedia.type === 'image' ? getImageUrl(activeMedia.id, 2560) : undefined"
                  :min-zoom="0.5"
                  :default-zoom="0.5"
                  :max-zoom="1.5"
                  show-open-externally-button
                  class="m-auto"
                  style="max-width: 640px"
                  square
                  preload-zoomable-img
                />
                <div v-else-if="activeMedia.type === 'video'" class="video-wrapper">
                  <iframe :key="activeMedia.id" :src="getYoutubeLink(activeMedia.id)" scrolling="no" frameBorder="0" allowfullscreen />
                </div>
              </template>
              <template v-else-if="creatingNewProduct">
                <ImprovedZoomableImage style="max-width: 640px" square />
              </template>

              <div v-if="shouldShowGallery" class="media-gallery minimalistic">
                <div v-for="media of allMedia" :key="media.id" class="position-relative">
                  <ImprovedProductImage
                    :src="media.type === 'image' ? getImageUrl(media.id, 256) : getYoutubeThumbnail(media.id)"
                    square
                    enforce-dimensions
                    class="link product p-2"
                    :class="[activeMedia?.id === media.id ? 'active' : 'inactive']"
                    :title="media.type === 'image' ? media.id : getYoutubeLink(media.id)?.split('?')?.[0]"
                    no-blend
                    :default-overlay-opacity="media.id === activeMedia?.id ? 1 : undefined"
                    @click="setActiveMedia(media.id, media.type)"
                  >
                    <template v-if="media.type === 'video'" #overlay>
                      <div class="middle center w-100">
                        <svg version="1.1" viewBox="0 0 68 48" width="100%" height="100%" class="video-overlay">
                          <path
                            d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                            fill="#f00"
                          ></path>
                          <path d="M 45,24 27,14 27,34" fill="#fff"></path>
                        </svg>
                      </div>
                    </template>
                  </ImprovedProductImage>
                  <div v-if="media.description" class="media-description">
                    {{ media.description }}
                  </div>
                </div>
              </div>

              <!-- Available in countries -->
              <PropertyComponent
                v-if="availableInCountriesProperty"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, availableInCountriesProperty)"
                class="mb-075"
                :class="{ 'available-countries': !editMode }"
                :edit-mode="editMode"
                :product="activeProduct"
                :product-input="productInput"
                :property="availableInCountriesProperty"
                show-property-info
                always-show-label
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                :show-if-contains="defaultPropertyVisibility"
                @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                @set="currentFieldHasStagedChanges = undefined"
                @contextmenu="openContextMenu($event, availableInCountriesProperty)"
              >
                <template #static="{ current }">
                  <div v-for="(country, i) of current?.defaultOptions" :key="country">
                    {{ current?.options?.[i] }}
                    <img :src="getCountryFlag(country as string)" class="tiny-flag" />
                  </div>
                </template>
              </PropertyComponent>

              <!-- Main language -->
              <PropertyComponent
                v-if="mainLanguageProperty"
                class="mb-4"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, mainLanguageProperty)"
                :property="mainLanguageProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                show-property-info
                always-show-label
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                :show-if-contains="defaultPropertyVisibility"
                @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                @set="currentFieldHasStagedChanges = undefined"
                @contextmenu="openContextMenu($event, mainLanguageProperty)"
              >
                <template #static="{ current }">
                  {{ current?.displayValue ?? current?.placeholder }}
                  <!-- prettier-ignore -->
                  <CultureSpecificAttribute v-if="!editMode && current?.options?.length" :language="(current?.defaultOptions?.[0] as Language)" />
                </template>
              </PropertyComponent>

              <!-- Youtube attributes -->
              <template v-if="editMode">
                <PropertyComponent
                  v-for="(property, i) of youtubeProperties"
                  :ref="property.defaultHeader"
                  :key="i"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, property)"
                  class="mb-075"
                  :property="property"
                  edit-mode
                  :product="activeProduct"
                  :product-input="productInput"
                  show-property-info
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, property)"
                />
              </template>
            </div>

            <!-------------------------->
            <!-- Product summary info -->
            <!-------------------------->

            <div class="col-md-12 col-lg-6 px-3 text-left summary" :style="`min-height: ${activeProduct.images?.gallery?.length ? presentationHeight : 0}px`">
              <!-- Name -->
              <PropertyComponent
                v-if="nameProperty"
                :ref="nameProperty.defaultHeader"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, nameProperty)"
                class="mb-3"
                :property="nameProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                show-property-info
                tag="h1"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                @set="currentFieldHasStagedChanges = undefined"
                @contextmenu="openContextMenu($event, nameProperty)"
              >
                <template #before="{ value }">
                  <CultureSpecificAttribute v-if="!editMode && value" class="mr-1" :language="currentCulture.language" />
                </template>
                <template #static="{ current }">
                  <h1 v-if="!current?.displayValue && !current?.status" class="missing-name w-100">
                    {{ translate(currentCulture.language, 'languages', 'capitalize').slice(0, -1) }}
                    {{ appTranslate('name missing', undefined, 'lowercase') }}
                  </h1>
                  <h1 v-else>{{ current.displayValue }}</h1>
                </template>
              </PropertyComponent>

              <!-- SKU -->
              <div class="mb-3">
                <PropertyComponent
                  v-if="skuProperty"
                  class="align-items-center"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, skuProperty)"
                  :property="skuProperty"
                  :product="activeProduct"
                  :product-input="productInput"
                  :edit-mode="editMode"
                  show-property-info
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, skuProperty)"
                >
                  <template #static>
                    <MoreReadableSku style="font-size: 20px" :sku="productInput?.sku ?? activeProduct.sku" :is-temporary-sku="activeProduct?.hasTemporarySku" />
                  </template>
                </PropertyComponent>
                <small class="small-errormsg">{{ skuError }}</small>
              </div>

              <!-- Recurrence ID -->
              <PropertyComponent
                v-if="recurrenceIdProperty"
                class="mb-3"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, recurrenceIdProperty)"
                :property="recurrenceIdProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                show-property-info
                activate-filter-links
                always-show-label
                :show-if-contains="defaultPropertyVisibility"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                @set="currentFieldHasStagedChanges = undefined"
                @contextmenu="openContextMenu($event, recurrenceIdProperty)"
              />

              <!-- Related products (static mode) -->
              <PropertyComponent
                v-if="activeProduct.relatedProducts?.length && !editMode && relatedProductsProperty"
                :ref="relatedProductsProperty.defaultHeader"
                class="mb-2"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, relatedProductsProperty)"
                :property="relatedProductsProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                show-property-info
                always-show-label
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                @set="currentFieldHasStagedChanges = undefined"
              >
                <template #static="{ current }">
                  <ul class="link-list" :class="[productInput ? productInput.propertyStatuses?.relationKey : activeProduct.propertyStatuses?.relationKey]">
                    <li v-for="relatedSku of current?.defaultOptions" :key="relatedSku">
                      <router-link
                        :to="{
                          name: 'product',
                          params: {
                            sku: relatedSku,
                          },
                          state: {
                            modal,
                          },
                        }"
                      >
                        <MoreReadableSku :sku="relatedSku.toString()" />
                      </router-link>
                    </li>
                  </ul>
                  <span class="mx-2">|</span>
                  <router-link
                    :to="{
                      name: 'products',
                      query: {
                        _relation: activeProduct.relationKey ?? activeProduct.sku,
                      },
                    }"
                  >
                    {{ appTranslate('Show all') }}
                  </router-link>
                </template>
              </PropertyComponent>

              <!-- Related item no SE (editmode) -->
              <div v-if="editMode" class="mb-4">
                <PropertyComponent
                  v-if="relationKeyProperty"
                  :ref="relationKeyProperty.defaultHeader"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, relationKeyProperty)"
                  :property="relationKeyProperty"
                  :product="activeProduct"
                  :product-input="productInput"
                  :edit-mode="editMode"
                  show-property-info
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, relationKeyProperty)"
                />
              </div>

              <!-- Availability status -->
              <PropertyComponent
                v-if="availabilityProperty"
                :ref="availabilityProperty.defaultHeader"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, availabilityProperty)"
                class="mb-3"
                :property="availabilityProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                show-property-info
                @contextmenu="openContextMenu($event, availabilityProperty)"
              >
                <template #static="{ value }">
                  <span class="availability-status" :style="`color: ${productAvailability?.colour}`">
                    <i class="fa" :class="productAvailability?.icon" />
                    {{ value }}
                  </span>
                </template>
              </PropertyComponent>

              <!-- Sellable web from -->
              <PropertyComponent
                v-if="sellableWebFromProperty"
                :ref="sellableWebFromProperty.defaultHeader"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, sellableWebFromProperty)"
                class="mb-3"
                :property="sellableWebFromProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                show-property-info
                :show-if-contains="defaultPropertyVisibility"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                always-show-label
                @contextmenu="openContextMenu($event, sellableWebFromProperty)"
              />

              <!-- Description(s) -->
              <label v-if="editMode" class="mb-3">
                <input v-model="showExtraDescriptions" type="checkbox" />
                <span class="ml-2 user-select-none">
                  {{ appTranslate('Show extra descriptions') }}
                </span>
              </label>

              <template v-for="property of descriptionProperties" :key="property.defaultName">
                <PropertyComponent
                  :ref="property.defaultHeader"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, property)"
                  :property="property"
                  :product="activeProduct"
                  :product-input="productInput"
                  :edit-mode="editMode"
                  class="mb-4 description"
                  :class="property.customPath"
                  show-property-info
                  :show-if-contains="defaultPropertyVisibility"
                  always-show-label
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, property)"
                />
              </template>

              <!-- Info text -->
              <PropertyComponent
                v-if="infoTextProperty"
                :ref="infoTextProperty.defaultHeader"
                :update-key="String(activeProduct.updateTime) + infoTextProperty.defaultHeader"
                :property="infoTextProperty"
                :product="activeProduct"
                class="mb-4"
                show-property-info
                :edit-mode="editMode"
                show-if-contains="value"
                always-show-label
              >
                <template v-if="selectedProductVersion">
                  {{ appTranslate('Info text is not available when a version is selected') }}
                </template>
              </PropertyComponent>

              <!-- USPs -->
              <ol
                v-if="
                  editMode ||
                  Object.keys(activeProduct.languageSpecific?.[currentCulture.language]?.usps || {})?.length ||
                  Object.keys(productInput?.languageSpecific?.[currentCulture.language]?.usps || {})?.length
                "
                class="usps mb-4"
              >
                <CultureSpecificAttribute v-if="!editMode" class="d-block" :language="currentCulture.language" />

                <PropertyComponent
                  v-for="(property, i) of uspProperties"
                  :key="i"
                  :ref="property.defaultHeader"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, property)"
                  class="mb-2"
                  :property="property"
                  :product="activeProduct"
                  :product-input="productInput"
                  :edit-mode="editMode"
                  show-property-info
                  :show-if-contains="defaultPropertyVisibility"
                  tag="li"
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, property, i)"
                  @paste="shiftIsPressed ? handleSpecialPaste($event, i) : 0"
                />
              </ol>

              <!-- Collections -->
              <PropertyComponent
                v-if="collectionsProperty"
                :ref="collectionsProperty.defaultHeader"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, collectionsProperty)"
                class="mb-2"
                :property="collectionsProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                always-show-label
                activate-filter-links
                show-property-info
                :show-if-contains="defaultPropertyVisibility"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @contextmenu="openContextMenu($event, collectionsProperty)"
              />

              <!-- Tags -->
              <PropertyComponent
                v-if="tagsProperty"
                :ref="tagsProperty.defaultHeader"
                :key="String(editMode)"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, tagsProperty)"
                class="mb-2"
                :property="tagsProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                always-show-label
                activate-filter-links
                :show-if-contains="defaultPropertyVisibility"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @contextmenu="openContextMenu($event, tagsProperty)"
              />
              <!-- Category tags -->
              <PropertyComponent
                v-if="categoryTagsProperty"
                :ref="categoryTagsProperty.defaultHeader"
                :key="String(editMode)"
                :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, categoryTagsProperty)"
                class="mb-2"
                :property="categoryTagsProperty"
                :product="activeProduct"
                :product-input="productInput"
                :edit-mode="editMode"
                always-show-label
                activate-filter-links
                :show-if-contains="defaultPropertyVisibility"
                :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                @contextmenu="openContextMenu($event, categoryTagsProperty)"
              />

              <p v-if="latestEditedRecords?.length && !selectedProductVersion" class="mt-2 font-italic">
                {{ appTranslate('Last modified') }}
                {{ latestEditedRecords?.[0] }}

                <InfoPopupComponent
                  v-if="latestEditedRecords?.length"
                  content-style="right: 0; white-space: nowrap;"
                  content-class="edited minimalistic minimalistic-thin"
                >
                  <ul class="m-0">
                    <li v-for="(record, i) of latestEditedRecords" :key="i" class="ml-2">
                      {{ record }}
                    </li>
                  </ul>
                </InfoPopupComponent>
              </p>
            </div>
          </div>
        </section>

        <!---------------------->
        <!-- Other attributes -->
        <!---------------------->
        <section class="info">
          <div class="info-header row" :style="`top: ${stickyTopMargin}`">
            <div class="col">
              <h3>{{ appTranslate('Other properties') }}</h3>
              <PropertySelectMenu
                v-model="selectedPropertyKeys"
                v-model:selected-deadlines="selectedDeadlines"
                :properties="extraProperties"
                :edit-mode="editMode"
                :selected-cultures="[currentCulture]"
                view="product"
                class="mb-1 w-fit-content"
                :property-keys-included-in-selected-deadlines="propertyKeysIncludedInSelectedDeadlines"
              />

              <!-- <em :class="{ invisible: !propertyFilter.hideEmpty && !propertyFilter.searchTerm }"> Visar filtrerat resultat </em> -->
            </div>

            <div class="col property-filter flex-wrap" style="min-width: 250px">
              <div class="mb-1 d-flex flex-wrap" style="gap: 0.5em; flex: 1 1 100px">
                <select
                  v-model="visibilityFilter"
                  class="d-inline-block form-control"
                  :class="{
                    'filter-active': !!visibilityFilter,
                  }"
                  style="flex: 1 1 200px"
                >
                  <option v-for="(option, i) of filterOptions" :key="i" :value="option.value">{{ option.displayValue }}</option>
                </select>

                <label
                  class="m-0"
                  :class="{
                    'filter-active': activateFilterLinks,
                    disabled: editMode,
                  }"
                  :title="appTranslate('Converts values into clickable filtering links')"
                >
                  <input v-model="activateFilterLinks" type="checkbox" :disabled="editMode" />
                  {{ appTranslate('Activate filter links') }}
                </label>
              </div>

              <label class="m-0 mb-auto w-100">
                <input
                  v-model.trim="propertySearchTerm"
                  type="search"
                  class="form-control"
                  :class="{
                    'filter-active': propertySearchTerm.trim(),
                  }"
                  :placeholder="`${appTranslate('Search')}...`"
                  @keydown.esc="handleEscape"
                />
              </label>
            </div>
          </div>

          <div class="other-properties">
            <div v-for="(propertyGroup, groupIndex) of extraPropertyGroups" :key="groupIndex" class="property-group">
              <div
                v-for="(property, propertyIndex) of propertyGroup"
                v-show="propertyShouldBeShown(property)"
                :key="propertyIndex"
                :class="[
                  {
                    'mb-075': editMode,
                  },
                ]"
              >
                <PropertyComponent
                  :ref="property.defaultHeader"
                  :update-key="propertyStore.getProductPropertyKey(activeProduct, productInput, property)"
                  class="property w-100 position-relative"
                  :property="property"
                  :product-input="productInput"
                  :product="activeProduct"
                  :edit-mode="editMode"
                  :activate-filter-links="activateFilterLinks"
                  always-show-label
                  show-property-info
                  :show-if-contains="visibilityFilter"
                  :undo-icon="creatingNewProduct ? null : 'fa-undo'"
                  @input="currentFieldHasStagedChanges = !!$event.containsChanges"
                  @set="currentFieldHasStagedChanges = undefined"
                  @contextmenu="openContextMenu($event, property)"
                >
                  <template v-if="toComparable(property.defaultName).includes('item no')" #static="{ value }">
                    <MoreReadableSku :sku="toSafeString(value)" :is-link="activateFilterLinks" />
                  </template>
                </PropertyComponent>
              </div>
            </div>
          </div>

          <small v-if="propertySearchTerm" class="d-inline-block mt-4">
            {{ appTranslate('Showing fields and values matching') }} <em>"{{ propertySearchTerm }}"</em>
          </small>
        </section>

        <section v-if="activeProduct && store.userPrivileges.has('export') && !editMode" class="extras">
          <ToggleComponent v-model="extras" :options="extraTabs" tabs class="mb-3" />

          <!-- EAN labels -->
          <template v-if="extras === 'ean-labels'">
            <EanDownloadComponent :product="activeProduct" :culture="currentCulture" style="overflow-x: auto" />
          </template>

          <!-- Flaps -->
          <template v-else-if="extras === 'flap'">
            <FlapDownloadComponent :product="activeProduct" :culture="currentCulture" :show-outline="false" />
          </template>

          <!-- Request document -->
          <template v-else-if="extras === 'request-document'">
            <button class="btn btn-light" @click="ExportService.getProductRequestDocument(activeProduct.sku, currentCulture.language)">
              <i class="fa fa-file-excel-o" /> {{ appTranslate('Generate request-document') }}
            </button>
          </template>
        </section>

        <section v-if="!editMode && store.userPrivileges.has('manageProducts')" class="d-flex gap-2 admin">
          <button class="btn btn-outline-danger" @click="deletePromptActive = true">
            {{ appTranslate('Delete') }}
          </button>

          <div class="d-flex gap-2 ml-auto">
            <small v-if="jeevesSync?.result" class="text-right">
              <strong v-if="jeevesSync.time">Synched {{ jeevesSync.time }}</strong>
              <div v-for="(target, i) of jeevesSync.result.targets" :key="i" class="text-capitalize" :class="[target.success ? 'text-success' : 'text-danger']">
                {{ target.name }}
                <i v-if="target.success" class="fa fa-check" />
                <i v-else class="fa fa-times text-danger" />
                <small v-if="target.err" class="font-italic"> {{ target.err }} </small>
              </div>
              <div v-if="jeevesSync.result.err" class="text-danger">
                {{ jeevesSync.result.err }}
              </div>

              <div v-if="jeevesSync.result.msg">
                {{ jeevesSync.result.msg }}
              </div>
            </small>

            <ActionPendingButton variant="warning" :pending="jeevesSync?.pending" @click="triggerJeevesUpdate()">
              <!-- :disabled="store.settings?.sync.jeeves.globalAutoSync === false" -->
              {{ appTranslate('Trigger Jeeves update') }}
            </ActionPendingButton>
          </div>
        </section>
      </template>

      <p v-else-if="error" v-html="error" />

      <ProductSkeleton v-else :main-img-height="presentationHeight" />

      <template v-if="modal && activeProduct" #footer>
        <ProductFooter
          v-model:current-culture="currentCulture"
          :product="activeProduct"
          :product-input="productInput"
          :edit-mode="editMode"
          :save-disabled="!!skuError || isPristine"
          :show-refresh-button="!creatingNewProduct"
          :loading="loading"
          :selected-product-version="selectedProductVersion"
          @edit="setEditMode(!editMode)"
          @commit="commitChanges()"
          @refresh="updateProduct(true)"
          @cancel="creatingNewProduct ? router.back() : setEditMode(false)"
          @trash="deleteProduct()"
          @show-versions="showVersionModal = true"
        />
      </template>

      <ProductFooter
        v-if="!modal && activeProduct"
        v-model:current-culture="currentCulture"
        :product="activeProduct"
        :product-input="productInput"
        :edit-mode="editMode"
        :save-disabled="!!skuError || isPristine"
        :show-refresh-button="!creatingNewProduct"
        class="py-3"
        :loading="loading"
        :selected-product-version="selectedProductVersion"
        @edit="setEditMode(!editMode)"
        @commit="commitChanges()"
        @refresh="updateProduct(true)"
        @cancel="creatingNewProduct ? router.back() : setEditMode(false)"
        @trash="deleteProduct()"
        @show-versions="showVersionModal = true"
      />

      <ContextMenuComponent
        v-if="contextMenu"
        :position="contextMenu.position"
        :options="contextMenu.options"
        class="context-menu"
        :margin-left-px="contextMenu.marginLeftPx"
        @close="contextMenu = undefined"
        @contextmenu.prevent
      >
        <strong v-if="contextMenu.header" class="mb-2" style="margin-left: 25px">
          {{ contextMenu.header }}
        </strong>
      </ContextMenuComponent>
    </component>

    <PromptComponent
      v-if="deletePromptActive && activeProduct?.sku"
      action="delete"
      :items="[activeProduct.sku]"
      :prompt="{ confirmString: 'DELETE', action: 'delete' }"
      @close="deletePromptActive = false"
      @confirm="deleteProduct()"
    >
      <template #header>
        <h5>{{ appTranslate('Delete') }} {{ activeProduct?.sku }}?</h5>
      </template>
    </PromptComponent>

    <ModalComponent v-if="showVersionModal && activeProduct" :backdrop="false" modal-style="height: 100%" animation="fade-in" @close="showVersionModal = false">
      <VersionSelectComponent :product="activeProduct" @view-version="setProductVersion" />
    </ModalComponent>
  </div>
</template>

<script lang="ts" setup>
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import ContextMenuComponent from '@/components/ContextMenuComponent.vue'
import EanDownloadComponent from '@/components/export/ean/EanDownloadComponent.vue'
import FlapDownloadComponent from '@/components/export/flaps/FlapDownloadComponent.vue'
import ImprovedZoomableImage from '@/components/ImprovedZoomableImage.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import ImprovedProductImage from '@/components/products/ImprovedProductImage.vue'
import ProductSearchComponent from '@/components/products/ProductSearchComponent.vue'
import PropertyComponent from '@/components/products/PropertyComponent.vue'
import PropertySelectMenu from '@/components/products/PropertySelectMenu.vue'
import ProductFooter from '@/components/products/single-product/ProductFooter.vue'
import ProductSkeleton from '@/components/products/single-product/ProductSkeleton.vue'
import VersionSelectComponent from '@/components/products/VersionSelectComponent.vue'
import PromptComponent from '@/components/PromptComponent.vue'
import CultureSpecificAttribute from '@/components/simple/CultureSpecificAttribute.vue'
import InfoPopupComponent from '@/components/simple/InfoPopupComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import MoreReadableSku from '@/components/simple/MoreReadableSkuComponent.vue'
import ProductMiniCard from '@/components/simple/ProductMiniCard.vue'
import ToggleComponent from '@/components/ToggleComponent.vue'
import { useBarcodes } from '@/composables/useBarcodes'
import { useSelectedProperties } from '@/composables/useSelectedProperties'
import { useTranslation } from '@/composables/useTranslation'
import { ContextMenu, ContextMenuOption } from '@/models/ContextMenu'
import { BarcodeOption } from '@/models/Ean'
import { BasicOption, Country, Language, Option } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import Message from '@/models/Message'
import { LanguageSpecifics, ProductImages, ProductType, SimplifiedProduct } from '@/models/Product'
import {
  BasicBaseProperty,
  EditableAdvancedProperty,
  EmptyCheckedPlaceholder,
  PropertyVisibility,
  SimplePropertySetterValueType,
  StoredProperty,
} from '@/models/Property'
import { SimplifiedAttribute } from '@/models/Taxonomy'
import { setDocumentTitle } from '@/router'
import DataService from '@/services/DataService'
import ExportService from '@/services/ExportService'
import ImportService from '@/services/ImportService'
import PropertyService from '@/services/PropertyService'
import { DefaultCulture, useDefaultStore } from '@/store'
import { usePropertyStore } from '@/store/propertyStore'
import { useTaxonomiesStore } from '@/store/taxonomiesStore'
import { browserStorage } from '@/utils/browserStorage'
import { getImageUrl, productHasCulturalData } from '@/utils/productUtils'
import {
  getBarcodeType,
  productPropertyIsDeletable,
  productPropertyIsEditable,
  productPropertyStatusIsEditable,
  propertyCanGenerateEan,
} from '@/utils/propertyUtils'
import { getCountryFlag, getProductTypeIconClass, getYoutubeId, getYoutubeLink, getYoutubeThumbnail, metaKey, toComparable, toSafeString } from '@/utils/tools'
import { ComputedRef, StyleValue, computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// -----------
// -- Setup --
// -----------
defineOptions({ name: 'ProductView' })

const props = defineProps<{
  sku?: string
  new?: boolean
  modal?: boolean
}>()

// --------------------------
// -- Composables & Stores --
// --------------------------
const store = useDefaultStore()
const taxonomiesStore = useTaxonomiesStore()
const route = useRoute()
const router = useRouter()
const { translate, appTranslate } = useTranslation()
const propertyStore = usePropertyStore()
const { checkBarcodeCollisions } = useBarcodes()

// ---------------
// -- Constants --
// ---------------
type MediaType = 'image' | 'video'
interface Media {
  id: string | undefined
  type: MediaType
  description?: string
}

const presentationHeight = 520
const creatingNewProduct = route.name === 'new-product'
const navigatedBackwards = !!history.state.forward

const filterOptions: Array<BasicOption<PropertyVisibility | undefined>> = [
  {
    displayValue: appTranslate('Show all selected fields'),
    value: undefined,
  },
  {
    displayValue: appTranslate('Show fields with value(s)'),
    value: 'value',
  },
  {
    displayValue: appTranslate('Show fields with value(s) or status'),
    value: 'any-data',
  },
  {
    displayValue: appTranslate('Show fields with value(s) or preliminary status'),
    value: 'value-or-preliminary',
  },
]
const paddingTop = '2em'

type Extras = 'ean-labels' | 'flap' | 'request-document'
const extraTabs: Array<Option<Extras>> = [
  {
    value: 'ean-labels',
    displayValue: appTranslate('EAN-labels'),
    experimental: true,
  },
  {
    value: 'flap',
    displayValue: translate('Flap', 'attributes', 'capitalize'),
    experimental: true,
  },
  {
    value: 'request-document',
    displayValue: appTranslate('Request-document'),
    experimental: true,
  },
]

// ---------------
// -- Reactives --
// ---------------
const loading = ref(false)
const activeMedia = ref<Media>()
const activeProduct = ref<SimplifiedProduct>()

const error = ref<string>()
const skuError = ref<string>()
const editMode = ref(false)
const productInput = ref<ProductInput>()

const visibilityFilter = ref(browserStorage.get<PropertyVisibility | undefined>('product-visibilityFilter'))

const actionPending = ref<'editProduct'>()
const resetKey = ref(0)
const productStartingPoint = ref<SimplifiedProduct>()
const includeAllCulturesSearch = ref()
const showExtraDescriptions = ref(false)
const activateFilterLinks = ref(!!browserStorage.get('product-activateFilterLinks'))

const stagedProductImages = ref<ProductImages>()
const contextMenu = ref<ContextMenu>()
const componentRef = ref<InstanceType<typeof ModalComponent>>()

const currentFieldHasStagedChanges = ref<boolean>() // used in isPristine to bridge the v-model:lazy gap

const extras = ref<Extras>('ean-labels')

const deletePromptActive = ref(false)

const showVersionModal = ref(false)
const jeevesSync = ref<{ result?: Awaited<ReturnType<typeof DataService.triggerJeevesUpdate>>; time?: string; pending: boolean }>()

const {
  propertySearchTerm,
  selectedCultures,
  properties,
  propertyGroups,
  selectedDeadlines,
  selectedPropertyKeys,
  propertyKeysIncludedInSelectedDeadlines,
  getPropertyByName,
  propertyShouldBeShown,
  initProperties,
} = useSelectedProperties('products', activeProduct, productInput)

const shiftIsPressed = ref(false)

// --------------------
// -- Computed Props --
// --------------------

const currentCulture = computed({
  get: () => selectedCultures.value[0],
  set: (value) => {
    selectedCultures.value = [value]
  },
})

const allMedia: ComputedRef<Media[]> = computed(() => {
  const activeImages = stagedProductImages.value || activeProduct.value?.images
  const images = [activeImages?.mainImg, activeImages?.gallery].flat().map((image) => ({
    id: image,
    type: 'image' as const,
  }))

  const videos =
    activeProduct.value?.attributes
      ?.filter((attribute) => toComparable(attribute.defaultName).startsWith('youtube') && attribute.options?.length)
      .sort((a, b) => a.name.toString().localeCompare(b.name.toString()))
      ?.map((attribute) => ({
        id: getYoutubeId(attribute.options?.map((option) => option.name)?.join() || ''),
        type: 'video' as const,
        ...(toComparable(attribute.defaultName).endsWith('eng') && {
          description: 'ENG',
        }),
      })) || []

  return [...images, ...videos].filter((media) => !!media.id)
})

const latestEditedRecords = computed(() => {
  const latestEditedRecords = activeProduct.value?.edited?.filter((record) => record.type !== 'script' && record.time).reverse()
  return latestEditedRecords?.map((record) => `${new Date(record.time).toLocaleString()} ${appTranslate('by %s', [record.user || 'unknown'], 'lowercase')}`)
})

const style = computed(() => {
  const style: StyleValue = {}

  if (editMode.value) {
    const borderColour = creatingNewProduct ? 'var(--success)' : 'orange'
    style.border = `3px solid ${borderColour}`
    if (!props.modal) {
      style.padding = '2em'
    }
  } else {
    style.height = '100%'
  }

  return style
})

const isPristine = computed(() => {
  const modifiedObjects = Object.keys(productInput.value || {}).length
  return !currentFieldHasStagedChanges.value && modifiedObjects === 0
})

// Properties used in summary section
const availableInCountriesProperty = computed(() => getPropertyByName('available in countries'))
const categoriesProperty = computed(() => getPropertyByName('categories'))
const tagsProperty = computed(() => getPropertyByName('tags'))
const categoryTagsProperty = computed(() => getPropertyByName('category tags'))
const collectionsProperty = computed(() => getPropertyByName('collections'))
const productTypeProperty = computed(() => getPropertyByName('product type'))
const nameProperty = computed(() => getPropertyByName('name', currentCulture.value.language))
const mainLanguageProperty = computed(() => getPropertyByName('main language'))
const skuProperty = computed(() => getPropertyByName('item no'))
const recurrenceIdProperty = computed(() => getPropertyByName('recurrence id'))
const relationKeyProperty = computed(() => getPropertyByName('related item no se'))
// const relatedProductsProperty = computed(() => getPropertyByName('related products'))
const relatedProductsProperty = computed(() => {
  // filtered out by 'products'
  const storedProp = propertyStore.getPropertyByKey<BasicBaseProperty>('related products')
  if (storedProp) {
    return PropertyService.getBasicProperty(storedProp)
  } else {
    console.log('could not find property related products')
  }
  return undefined
})

const availabilityProperty = computed(() => getPropertyByName('availability status'))
const sellableWebFromProperty = computed(() => getPropertyByName('sellable web from'))
const infoTextProperty = computed(() => getPropertyByName('info text', currentCulture.value.language))

const youtubeProperties = computed(() => properties.value?.filter((p) => p.defaultName.toLowerCase().includes('youtube')))
const uspProperties = computed(() =>
  properties.value?.filter((p) => p.defaultName.toLowerCase().includes('usp') && p.culturePrefix === currentCulture.value.language),
)
const descriptionProperties = computed(
  () =>
    properties.value?.filter(
      (property) => property.defaultName.toLowerCase().includes('description') && property.culturePrefix === currentCulture.value.language,
    ) as EditableAdvancedProperty<'languageSpecific'>[],
)

const defaultPropertyVisibility = computed((): PropertyVisibility | undefined => (editMode.value ? undefined : 'value-or-preliminary'))

const stickyTopMargin = computed(() => {
  if (props.modal) {
    return `calc(-${paddingTop} - 1px)`
  }
  return `${store.topMenuHeight}px`
})

const productAvailability = computed(() =>
  store.settings?.properties?.productAvailabilityOptions.find(
    (o) => o.code == (productInput.value?.availability?.code ?? activeProduct.value?.availability?.code),
  ),
)

const selectedProductVersion = computed(() => route.query.version?.toString())

const shouldShowGallery = computed(() => {
  const mainImg = stagedProductImages.value?.mainImg || activeProduct.value?.images?.mainImg
  return allMedia.value.length > 1 || !mainImg
})

const extraPropertyGroups = computed(() =>
  propertyGroups.value?.map((group) =>
    group.filter(
      (property) => !property.hideInInfoSection && (!property.exclusiveToCountries || property.exclusiveToCountries.includes(currentCulture.value.country)),
    ),
  ),
)

const extraProperties = computed(() => extraPropertyGroups.value?.flat() || [])

// --------------
// -- Watchers --
// --------------

watch(
  () => visibilityFilter.value,
  (value) => {
    if (value) {
      browserStorage.set('product-visibilityFilter', value)
    } else {
      browserStorage.delete('product-visibilityFilter')
    }
  },
)

watch(activateFilterLinks, (value) => {
  browserStorage.set('product-activateFilterLinks', value)
})

watch(
  currentCulture,
  (newCulture) => {
    if (productHasCulturalData(activeProduct.value, newCulture)) {
      setDocumentTitle(`${`${activeProduct.value?.sku} ${activeProduct.value?.languageSpecific?.[currentCulture.value.language]?.name || ''}`.trim()}`)
    }
  },
  { deep: true },
)

watch(
  () => productInput.value?.sku,
  async () => {
    if (productInput.value?.sku && productInput.value.sku !== activeProduct.value?.sku) {
      // look for images in image library
      if (productInput.value?.sku && productInput.value?.sku.length >= 6) {
        DataService.getAvailableImgsForProduct(productInput.value.sku).then((imgs) => {
          stagedProductImages.value = imgs
          setActiveMedia(stagedProductImages.value?.mainImg, 'image')
        })
      } else {
        stagedProductImages.value = undefined

        if (activeMedia.value) {
          setActiveMedia(activeProduct.value?.images?.mainImg, 'image')
        }
      }

      // check for existing sku
      const sku = productInput.value?.sku || activeProduct.value?.sku
      if (sku && editMode.value) {
        const existingProduct = await DataService.getProduct(sku, true, undefined, undefined, undefined, {
          //  includeVersions: '1' // 260306: disabled versions since its not currently used
          doNotUseStandardOptionsAsFallback: 1,
        })
        if (existingProduct) {
          skuError.value = appTranslate('There is already a product with the same item number')
        } else {
          skuError.value = undefined
        }
      }
      // }, 500)
    } else {
      skuError.value = undefined
      stagedProductImages.value = undefined
      if (activeMedia.value) {
        setActiveMedia(activeProduct.value?.images?.mainImg, 'image')
      }
    }
  },
)

watch(
  () => store.dataCulture,
  (newValue) => {
    if (newValue) {
      currentCulture.value = newValue
      ensureAvailableCulture(activeProduct.value)
    }
  },
  { deep: true },
)

watch([() => store.translationLanguage, () => selectedProductVersion.value], () => {
  updateProduct()
})

watch(
  () => propertyStore.storedProperties,
  () => {
    initProperties(true)
  },
)

// ---------------------
// -- Lifecycle hooks --
// ---------------------
onMounted(async () => {
  document.addEventListener('beforeunload', (e) => {
    e.preventDefault()
    e.returnValue = false
  })

  await initProperties()

  currentCulture.value = store.dataCulture || DefaultCulture

  if (creatingNewProduct) {
    setCreateNew()
  } else {
    await updateProduct()
  }

  window.addEventListener('keydown', shiftKeyHandler)
  window.addEventListener('keyup', shiftKeyHandler)
})

onUnmounted(() => {
  window.removeEventListener('keydown', shiftKeyHandler)
  window.removeEventListener('keyup', shiftKeyHandler)
})

const shiftKeyHandler = (e: KeyboardEvent) => {
  if (e.key !== 'Shift' || e.repeat) {
    return
  }

  shiftIsPressed.value = e.type === 'keydown'
}
// ---------------
// -- Functions --
// ---------------

const updateProduct = async (recache?: boolean) => {
  if (!props.sku) {
    return
  }

  loading.value = true

  if (route.query.recache !== undefined) {
    // trigger recache on route navigation
    await router.replace({
      name: 'product',
      params: { sku: props.sku },
    }) // remove recache query from url
    recache = true
  }

  error.value = undefined

  const product = await DataService.getProduct(
    props.sku,
    true,
    {
      dataLanguage: currentCulture.value.language,
      dataCountry: currentCulture.value.country,
      includeCultureExclusiveData: true,
    },
    recache,
    undefined,
    {
      // includeVersions: '1', // 260306: disabled versions since its not currently used
      doNotUseStandardOptionsAsFallback: 1,
    },
  )

  if (product) {
    if (!editMode.value && !productHasCulturalData(product, currentCulture.value)) {
      ensureAvailableCulture(product)
    }

    if (selectedProductVersion.value) {
      await updateProductVersion(product)
    } else {
      activeProduct.value = product

      if (!editMode.value) {
        productInput.value = undefined
      }
    }

    setDocumentTitle(`${`${product?.sku} ${product?.languageSpecific?.[currentCulture.value.language]?.name || ''}`.trim()}`)

    const descriptionHasValueOrPreliminary = (descriptionType: keyof LanguageSpecifics) => {
      return !!(
        product?.languageSpecific?.[currentCulture.value.language]?.[descriptionType] ||
        product?.propertyStatuses?.[['languageSpecific', currentCulture.value.language, descriptionType].join('/')] === 'preliminary'
      )
    }

    showExtraDescriptions.value =
      !!descriptionHasValueOrPreliminary('shortDescription') ||
      !!descriptionHasValueOrPreliminary('longDescription') ||
      !!descriptionHasValueOrPreliminary('stmDescription')

    if (!activeMedia.value || recache) {
      setActiveMedia(product.images?.mainImg, 'image')
    }
    await initProperties(true)

    store.lastActiveProduct = product
  } else {
    error.value = appTranslate('Could not find product %s', [`<em>${props.sku}</em>`])
  }

  loading.value = false
}

const updateProductVersion = async (startingPoint: SimplifiedProduct) => {
  if (selectedProductVersion.value) {
    const productVersion = await DataService.getProductVersion(startingPoint.sku, selectedProductVersion.value)
    activeProduct.value = startingPoint

    if (!productVersion) {
      return
    }

    productInput.value = {}
    activeProduct.value = {
      sku: '',
      type: 'product',
      images: startingPoint.images, // needed to continue displaying images
      edited: startingPoint.edited, // needed for version to display correct editor
      versions: startingPoint.versions, // needed for version to display all versions
      // relatedProducts: startingPoint.relationKey === productVersion.relationKey ? startingPoint.relatedProducts : undefined,
      categories: [],
      tags: [],
      collections: [],
      attributes: [],
    }

    properties.value?.forEach((property) => {
      const result = property.get(undefined, productVersion)

      if (property.set(productInput.value, result?.defaultOptions ?? null, activeProduct.value)) {
        propertyStore.incPropertyResetKey(startingPoint.sku, property.key)
      }
    })

    activeProduct.value.sku = startingPoint.sku // set sku after to make sure sku property is editable in property setter
  }
}

// Edit-methods
const setEditMode = async (edit: boolean) => {
  propertyStore.resetStagedPropertyOptions()

  if (edit) {
    taxonomiesStore.clearTaxonomicCache()

    if (!activeProduct.value) {
      store.addMessage(new Message('Could not find product to edit', undefined, 'danger'))
      return
    }

    void DataService.loadAvailableBarcodeOptions(true)
    void DataService.loadPropertyInstructions(true)

    await Promise.all([updateProduct()])
    productInput.value = {}
    store.confirmNavigationFrom = route.path

    editMode.value = true
  } else if (isPristine.value || confirm(`${appTranslate('Do you want to exit the editing mode')}? ${appTranslate('Your changes will not be saved')}.`)) {
    await updateProduct()
    productInput.value = undefined
    store.confirmNavigationFrom = undefined
    ensureAvailableCulture(activeProduct.value)
    editMode.value = false
  }
}

const setCreateNew = async () => {
  try {
    store.confirmNavigationFrom = route.path

    editMode.value = true
    productInput.value = await ImportService.getProductStartingPoint()
    activeProduct.value = {} as SimplifiedProduct

    setActiveMedia(undefined, 'image')
  } catch (err: any) {
    console.error(err)
  }
}

const setProductStartingPoint = async (product?: SimplifiedProduct) => {
  if (productInput.value) {
    if (product) {
      const productName = product?.languageSpecific?.[currentCulture.value.language]?.name || product.sku
      if (isPristine.value || confirm(appTranslate('Do you want to load data from %s?', [productName]))) {
        // Unlike when editing an existing product, setting a starting point requires the product input to be fully populated (existing products)
        productStartingPoint.value = product

        try {
          productInput.value = await ImportService.getProductStartingPoint(product.sku)
          activeProduct.value = {} as SimplifiedProduct
          ensureAvailableCulture(activeProduct.value)
          resetKey.value++
        } catch (err: any) {
          console.log(err)
          store.addMessage(new Message(appTranslate('An error occurred'), err?.response?.data, 'danger'))
        }
      }
    } else if (confirm(appTranslate('Do you want to clear entered data?'))) {
      productInput.value = await ImportService.getProductStartingPoint()
      activeProduct.value = {} as SimplifiedProduct

      productStartingPoint.value = undefined
      resetKey.value++
    }
  }
}

const commitChanges = async () => {
  if (actionPending.value || !productInput.value || !activeProduct.value) {
    return
  }

  if (productInput.value.sku) {
    const skuExists = await DataService.getProduct(productInput.value.sku, true)
    if (skuExists) {
      store.addMessage(new Message(appTranslate('Item no already taken'), undefined, 'danger'))
      return
    }
  }

  actionPending.value = 'editProduct'
  let sku

  try {
    store.confirmNavigationFrom = undefined
    sku = productInput.value.sku?.replace(/\s/g, '') || activeProduct.value.sku || (await ImportService.getTemporarySkus(1))?.[0]

    const changedSku = !creatingNewProduct && productInput.value.sku && productInput.value.sku !== activeProduct.value.sku

    const barcodeCollisionCheck = await checkBarcodeCollisions([productInput.value])
    if (!barcodeCollisionCheck.ok && !confirm(barcodeCollisionCheck.msg)) {
      return
    }

    await ImportService.importProducts({ [sku]: productInput.value }, undefined, { all: true }, false, 'singularProduct')

    await updateProduct()
    taxonomiesStore.clearTaxonomicCache()

    const message = appTranslate(`The product %s has been ${creatingNewProduct ? 'created' : 'edited'}`, [sku])

    store.addMessage(new Message(message, undefined, 'success', 5))

    if (creatingNewProduct || changedSku) {
      router.replace({
        name: 'product',
        state: { modal: true },
        params: {
          sku,
        },
      })
    } else {
      editMode.value = false
      productInput.value = undefined
      updateProduct()
    }
  } catch (err) {
    console.log(err)
    store.addMessage(new Message(appTranslate('An error occurred'), err, 'danger'))
  } finally {
    actionPending.value = undefined
  }
}

const deleteProduct = async () => {
  // if (activeProduct.value?.sku && confirm(`${appTranslate('Are you sure you want to discard the product')}?`)) {
  if (activeProduct.value && (await DataService.deleteProduct(activeProduct.value.sku))) {
    store.addMessage(new Message(appTranslate('The product %s has been removed', [activeProduct.value.sku]), undefined, 'success', 5))
    router.back()
  }
  // }
}

const ensureAvailableCulture = (product: SimplifiedProduct | undefined) => {
  if (!productHasCulturalData(product, currentCulture.value)) {
    const availableCulture = [store.dataCulture, ...store.availableCultures].find((culture) => culture && productHasCulturalData(product, culture))
    // watchers triggers update
    currentCulture.value = availableCulture || store.dataCulture || DefaultCulture

    if (!availableCulture) {
      loading.value = false
      activeProduct.value = product
    }
  }
}

const setActiveMedia = (value: string | undefined, type: MediaType) => {
  activeMedia.value = {
    type,
    id: value,
  }
}

const handleEscape = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement
  event.stopPropagation()
  if (!propertySearchTerm.value.length) {
    target?.blur()
  }
}

const generateBarcode = async (property: EditableAdvancedProperty, option?: BarcodeOption) => {
  const barcodeType = getBarcodeType(property)

  const sku = productInput.value?.sku ?? activeProduct.value?.sku
  if (productInput.value && sku && barcodeType) {
    const response = await DataService.generateEanCodes([productInput.value], [barcodeType], option?.id)
    const barcode = response.codes[sku]?.[barcodeType]

    if (barcode) {
      setProductPropertyValue(property, barcode)
    } else {
      if (response.failures.categorization.length) {
        store.addMessage(
          new Message(
            appTranslate('Failed to automatically categorize %s products for EAN-code generation', [response.failures.categorization?.length]),
            response.failures.categorization?.join(', '),
            'warning',
          ),
        )
      } else if (response.failures.generation.length) {
        store.addMessage(
          new Message(
            appTranslate('Could not generate %s%s for %s', [property.header || property.defaultHeader, option?.title ? ` "${option?.title}"` : '', sku]),
            undefined,
            'warning',
          ),
        )
      }
    }
  }
}

const openContextMenu = (event: MouseEvent, property: EditableAdvancedProperty, uspIndex?: number) => {
  if (contextMenu.value?.id === property.defaultHeader) {
    contextMenu.value = undefined
  } else if (editMode.value) {
    event.preventDefault()
    const edited = property.get(undefined, productInput.value)

    const propertyStatusPath = property.sortFieldPath.replaceAll('.', '/')

    const generateEanOptionsWithSeparator: ContextMenuOption[] = propertyCanGenerateEan(property)
      ? [
          {
            type: 'separator',
          },
          {
            type: 'action',
            icon: 'fa-barcode',
            label: `${appTranslate(`Generate EAN13`)}`,
            callback: () => generateBarcode(property),
            collapsedItems: store.availableBarcodeOptions?.map((option) => ({
              label: appTranslate('Use "%s"', [translate(option.title, 'ean')]),
              callback: () => generateBarcode(property, option),
              icon: 'fa-exclamation-triangle',
              disabled: option.disabled,
            })),
          },
        ]
      : []

    let modalLeftMargin = 0
    let modalTopMargin = 0
    const modal = componentRef.value?.$el?.querySelector('.custom-modal')

    if (modal) {
      const result = getComputedStyle(modal)
      modalLeftMargin = (window.innerWidth - parseInt(result.width)) / 2
      modalTopMargin = (window.innerHeight - parseInt(result.height)) / 2
    }

    const propertyIsEditable = !productPropertyIsEditable(property, activeProduct.value, productInput.value)

    const translatedMetaKey = appTranslate(metaKey)
    const shiftKey = appTranslate('Shift')

    const pasteOptions: ContextMenuOption[] = [
      {
        type: 'action',
        label: appTranslate('Paste'),
        icon: 'fa-paste',
        callback: async () => {
          const value = await navigator.clipboard?.readText()
          setProductPropertyValue(property, value)
        },
        keyboardShortcut: `${translatedMetaKey}+V`,
        disabled: !!propertyIsEditable,
      },
    ]

    if (uspIndex !== undefined) {
      pasteOptions.push({
        type: 'action',
        label: appTranslate('Paste special'),
        icon: 'fa-paste',
        callback: async () => {
          const value = await navigator.clipboard?.readText()
          pasteUSPs(value, uspIndex)
        },
        keyboardShortcut: `${translatedMetaKey}+${shiftKey}+V`,
        info: appTranslate('Pastes your clipboard split on line breaks to the USP fields\nstarting from the selected field'),
        disabled: !!propertyIsEditable,
      })
    }

    contextMenu.value = {
      id: property.defaultHeader,
      header: PropertyService.getTranslatedHeader(property, store.translationLanguage),
      position: {
        x: event.x - modalLeftMargin,
        y: event.y - modalTopMargin,
      },
      marginLeftPx: modalLeftMargin,
      options: [
        ...pasteOptions,
        { type: 'separator' },
        {
          type: 'action',
          label: appTranslate('Reset'),
          icon: 'fa-undo',
          callback: () => {
            property.reset(productInput.value, activeProduct.value)
            property.resetStatus(productInput.value)
            propertyStore.incPropertyResetKey(activeProduct.value?.sku, property.key)
          },
          disabled:
            !productPropertyIsEditable(property, activeProduct.value, productInput.value) ||
            (!currentFieldHasStagedChanges.value && edited?.displayValue === undefined && !edited?.delete && edited?.status === undefined),
        },
        {
          type: 'action',
          label: appTranslate('Delete'),
          icon: 'fa-trash-o',
          callback: () => {
            property.setStatus(productInput.value, null, activeProduct.value)
            setProductPropertyValue(property, null)
          },
          disabled: !productPropertyIsDeletable(property, activeProduct.value, productInput.value) || (edited?.delete && !edited.status),
        },
        {
          type: 'separator',
        },
        {
          type: 'action',
          label: `<span class="preliminary-text">${appTranslate('Set preliminary status')}</span>`,
          callback: () => {
            property?.setStatus(productInput.value, 'preliminary', activeProduct.value)
            propertyStore.incPropertyResetKey(activeProduct.value?.sku, property.key)
          },
          disabled: !productPropertyStatusIsEditable('preliminary', property, activeProduct.value, productInput.value),
        },
        {
          type: 'action',
          label: `${appTranslate('Set to %s', [`<span class="not-applicable-text">${EmptyCheckedPlaceholder}</span>`])}`,
          info: appTranslate('Clears value(s) and sets status checked'),
          callback: () => {
            property.setStatus(productInput.value, 'checked', activeProduct.value)
            setProductPropertyValue(property, null)
          },
          // disabled: property.readonly,
          disabled: !productPropertyStatusIsEditable('checked', property, activeProduct.value, productInput.value),
        },
        {
          type: 'action',
          label: appTranslate('Remove status'),
          icon: 'fa fa-times',
          callback: () => {
            property.setStatus(productInput.value, null, activeProduct?.value)
            propertyStore.incPropertyResetKey(activeProduct.value?.sku, property.key)
          },
          // disabled: property.readonly && !isSkuProperty(property),
          disabled:
            !productPropertyStatusIsEditable(null, property, activeProduct.value, productInput.value) ||
            (!productInput.value?.propertyStatuses?.[propertyStatusPath] && !activeProduct.value?.propertyStatuses?.[propertyStatusPath]),
        },
        ...generateEanOptionsWithSeparator,
      ],
    }
  }
}

const handleClose = () => {
  if (history.state.back) {
    router.back()
  } else {
    router.replace({ name: 'products' })
  }
}

const setProductVersion = async (version?: string) => {
  showVersionModal.value = false

  if (selectedProductVersion.value !== version) {
    if (version) {
      await router.replace({ query: { version }, state: { modal: props.modal } })
    } else {
      await router.replace({ state: { modal: props.modal } })
    }
  }
}

const triggerJeevesUpdate = async () => {
  if (activeProduct.value) {
    jeevesSync.value = { pending: true }
    jeevesSync.value = { result: await DataService.triggerJeevesUpdate([activeProduct.value.sku]), time: new Date().toLocaleTimeString(), pending: false }
  }
}

const pasteUSPs = (value: string, propertyStartIndex: number) => {
  if (!activeProduct.value || !productInput.value || !value) {
    return
  }

  const split = value.trim().split(/\r?\n/)

  for (let i = 0, propertyIndex = propertyStartIndex; i < split.length && propertyIndex <= 5; i++, propertyIndex++) {
    const value = split[i]
    const property = uspProperties.value?.[propertyIndex]

    if (property) {
      setProductPropertyValue(property, value)
    }
  }
}

const handleSpecialPaste = (e: ClipboardEvent, uspStartIndex: number) => {
  const value = e.clipboardData?.getData('text/plain')
  if (value !== undefined) {
    pasteUSPs(value, uspStartIndex)
    e.preventDefault()
  }
}

const setProductPropertyValue = (property: EditableAdvancedProperty, value: SimplePropertySetterValueType) => {
  if (!activeProduct.value) {
    return
  }
  property.set(productInput.value, value, activeProduct.value)
  propertyStore.incPropertyResetKey(activeProduct.value.sku, property.key)
}
</script>

<style lang="scss" scoped>
:deep() {
  section {
    padding-bottom: 2em;

    &:not(:first-child) {
      padding-top: 2em;
    }
  }

  section:not(:first-child) {
    border-top: 1px solid #ddd;
  }

  section.presentation {
    .category-breadcrumb:not(:last-child) span::after {
      content: '/';
      margin: 0 1em;
      user-select: none;
    }

    .video-wrapper {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      aspect-ratio: 1;

      iframe {
        width: 100%;
        aspect-ratio: 16 / 9;
      }
    }

    .summary {
      p:not([contenteditable='true']) {
        margin-bottom: 1.5em;
      }

      // ol,
      ul {
        list-style-type: none;
        padding: 0;
      }
    }

    h1 {
      @media (max-width: 575px) {
        font-size: 2em;
        word-break: break-word;
      }
      &.missing-name {
        color: var(--light-grey);
        user-select: none;
      }
    }

    .media-gallery {
      display: flex;
      flex-wrap: wrap;
      overflow: auto;

      > * {
        width: 50% !important;

        .video-overlay {
          max-width: 25%;
          max-height: 25%;
          filter: saturate(0);
        }

        &.active .video-overlay,
        &:hover .video-overlay {
          filter: saturate(1);
        }

        @media (min-width: 440px) {
          width: 33% !important;
        }

        @media (min-width: 768px) {
          width: 25% !important;
        }
      }

      .media-description {
        position: absolute;
        bottom: 5px;

        width: 100%;
        text-align: center;

        font-size: 0.65em;
        font-weight: bold;
      }
    }

    .edited {
      font-style: italic;
      max-height: 400px;
      overflow: auto;
      margin: 0;
      padding: 0.5rem;
    }
  }

  .product.edit-mode .description *[contenteditable='true'] {
    min-height: 7em;
    max-height: 25em;
  }

  .product:not(.edit-mode) .description {
    white-space: pre-line;

    &.shortDescription,
    &.longDescription,
    &.stmDescription {
      position: relative;
      padding: 0 0.5em 0.5em;
      margin: 0 -0.5em;
      border: 1px solid #ddd;

      &::after {
        position: absolute;
        bottom: 0;
        right: 5px;
        font-size: 10px;
        font-weight: bold;
      }
    }

    &.shortDescription {
      background-image: linear-gradient(20deg, white 10%, rgb(199 175 111 / 25%));
      &::after {
        content: 'Short';
      }
    }

    &.longDescription {
      background-image: linear-gradient(20deg, white 10%, rgba(117, 111, 199, 0.25));
      &::after {
        content: 'Long';
      }
    }

    &.stmDescription {
      background-image: linear-gradient(20deg, white 10%, rgba(113, 176, 201, 0.25));
      &::after {
        content: 'STM';
      }
    }
  }

  .product:not(.edit-mode) .usps img {
    margin-left: -1em;
  }

  .description,
  .usps {
    border-radius: 2px;
  }

  .edit-mode .usps {
    list-style-type: none;
    padding: 0;
  }

  .product:not(.edit-mode) .usps {
    list-style-type: disc;
    border: 1px solid #ddd;
    margin: 0 -0.5em;
    padding: 0 2em;
    width: 100%;
  }

  .edit-mode {
    section.info .other-properties {
      columns: 1;
    }

    h2 {
      color: orange;
    }
  }

  // .custom-modal section.info .info-header {
  //   top: calc(var(--productview-padding-top) * -1 - 1px); // makeshift solution for sticky positioning with modal (removes 2em padding)
  // }

  section.info {
    min-height: 1000px;

    .info-header {
      position: sticky;
      top: 0;
      z-index: 1;
      padding: 1em 0 0.5em;
      // margin-bottom: 1.5em;
      display: flex;
      background-color: white;

      .property-filter {
        display: flex;
        padding-left: 10px;
        margin-right: 10px;

        > * {
          display: flex;
          align-items: center;
        }

        label {
          font-weight: unset;
          margin-right: 0.5em;
          user-select: none;
        }

        select,
        option {
          font-size: 0.95em;
          padding: 4px;
        }
      }
    }

    // used by skeleton as well
    .other-properties {
      columns: 1;

      .property-group {
        margin-bottom: 2em;

        .property {
          display: flex;
          flex-wrap: wrap;
          // margin-bottom: 2px;
          break-inside: avoid;
        }
      }

      @media (min-width: 576px) {
        columns: 2;
      }
    }
  }

  section.extras {
    padding-top: 0.5em;
    min-height: 500px;
  }

  section.admin {
    button {
      height: min-content;
    }
  }

  .create-new-separator {
    // border-bottom: 6px dotted var(--success);
    border-bottom: 2px solid var(--success);
    box-shadow: 0 8px 10px -10px #aaa;
    padding-bottom: 2em;
  }

  h1,
  h2,
  h3,
  h4,
  h5 {
    text-shadow: 0 3px 3px #eee;
  }

  // .status-static {
  .availability-status {
    // margin-bottom: 15px !important;
    font-weight: bold;
    font-size: 1.5em;

    // &.complete {
    //   color: #5c5;
    // }
    // &.incomplete {
    //   color: var(--incomplete);
    // }
  }

  .link-list {
    display: inline;

    li {
      display: inline-block;

      &:not(:last-child)::after {
        content: ',';
        margin-right: 0.5em;
      }
    }
  }

  button.action {
    height: fit-content;
  }

  h2 {
    font-weight: bold;
  }

  .create-mode {
    h2 {
      color: var(--success);
      margin-bottom: 1em;
    }
  }

  .modal-shadow {
    position: absolute;
    top: 0;
    left: 0;
  }

  .shadow-top {
    z-index: 2;
    height: 20px;
    width: 100%;
    box-shadow: rgb(170 170 170) 0 15px 15px -15px inset;
  }

  .shadow-left {
    height: 100%;
    width: 20px;
    box-shadow: rgb(170 170 170) 15px 0 15px -15px inset;
  }

  .shadow-right {
    right: 0;
    left: unset;
    height: 100%;
    width: 20px;
    box-shadow: rgb(170 170 170) -15px 0 15px -15px inset;
  }

  .select-culture {
    display: inline-block;
    width: auto;
    height: unset;
    margin-top: 0.5em;
    padding: 2px;
  }

  .mb-075 {
    margin-bottom: 0.75rem;
  }

  .categories {
    display: grid;
    column-gap: 5px;
    grid-template-columns: 1fr;

    @media (min-width: 992px) {
      grid-template-columns: repeat(3, 1fr);

      .category {
        height: fit-content;
      }
    }
  }

  .available-countries .state {
    display: inline-flex;
    flex-wrap: wrap;

    :not(:last-child)::after {
      content: '|';
      margin-inline: 0.5em;
      color: #aaa;
    }
  }
}
</style>
